import React from 'react';
import {graphql, useFragment} from 'react-relay';
import {useIntl} from 'react-intl';
import Styled from 'styled-components/macro';
import {RadioButton} from '@/web-components';
import SettingsProjectSubNav from './SettingsProjectSubNav';
import {createToast} from '../../shared/components/toasts/toast';
import UpdateCompanyMutation from '../../../mutations/update_company_mutation.modern';
import Util from '../../shared/util/util';
import * as tracking from '../../../tracking';
import {ForecastThemeProvider} from '../../shared/ForecastThemeProvider';
import {TASK_MONITORING} from '../../../constants';

const Page = Styled.div`
	max-width: 1000px;
	margin-left: 24px;
	margin-top: 6px;
	font-size: 13px;
`;

const SectionDescription = Styled.div`
	margin-bottom: 16px;
	max-width: 620px;
`;

const OptionWrapper = Styled.div`
	margin-bottom: 12px;
`;

const OptionLine = Styled.div`
	display: flex;
	align-items: center;
`;

const OptionLabel = Styled.span`
	margin-left: 8px;
	font-weight: 500;
`;

// 38px so the description starts on the same x as the label above it, rather than sitting
// 10px to its left. RadioButton is 22px wide (border-box) and carries an 8px margin-right,
// and OptionLabel adds its own 8px margin-left: 22 + 8 + 8.
const OptionDescription = Styled.div`
	margin-left: 38px;
	color: #a1a1a1;
`;

const ScopeNote = Styled.div`
	margin-top: 24px;
	max-width: 620px;
	color: #a1a1a1;
`;

// PIKA-558: only rendered in the both-flags-set state, which no UI can produce.
const BothSetWarning = Styled.div`
	margin-bottom: 16px;
	max-width: 620px;
	color: #d0021b;
`;

// PIKA-558. The company default and the per-project override are the same decision made
// twice, so the two booleans are always written as a pair — exactly as changeTaskMonitoring
// does at project level. That is what keeps the three options mutually exclusive; the data
// model does not enforce it.
export const changeTaskMonitoringDefault = (companyId, option, formatMessage) => {
	Util.CommitMutation(
		UpdateCompanyMutation,
		{
			id: companyId,
			defaultUseTaskOwner: option === TASK_MONITORING.USE_OWNER,
			defaultUseTaskFollowers: option === TASK_MONITORING.USE_FOLLOWERS,
		},
		data => {
			if (data) {
				// Tracked inside the success callback, not before the commit, so a rejected
				// mutation is not counted as a change. Failures themselves surface through
				// Util.CommitMutation's standard MUTATION_ERROR modal, and Relay rolls back
				// the optimistic radio move, so no extra onError is needed here.
				tracking.trackEvent('Settings - Task Monitoring Default - Changed', {value: option});
				createToast({
					duration: 5000,
					message: formatMessage({id: 'common.changes_saved'}),
				});
			}
		}
	);
};

export const SettingsTaskMonitoringDefaultComponent = ({viewer}) => {
	const {formatMessage} = useIntl();
	const {id: companyId, defaultUseTaskOwner, defaultUseTaskFollowers} = viewer.company;

	// PIKA-558: do NOT collapse this with a nested ternary. Owner would silently win when
	// both flags are true, so the screen would misreport what is stored — the exact fault
	// this PR fixes in footer_section. That state is reachable: UpdateCompanyInput exposes
	// the two booleans independently, so anything hitting the mutation or the REST company
	// endpoint outside this screen can set both. Surface it instead of resolving it, or an
	// Admin can neither see it nor diagnose it — and clicking any radio would overwrite it.
	const bothSet = defaultUseTaskOwner && defaultUseTaskFollowers;
	const selected = bothSet
		? null
		: defaultUseTaskOwner
		? TASK_MONITORING.USE_OWNER
		: defaultUseTaskFollowers
		? TASK_MONITORING.USE_FOLLOWERS
		: TASK_MONITORING.NO_TASK_MONITORING;

	const select = option => changeTaskMonitoringDefault(companyId, option, formatMessage);

	return (
		<div className="section-content settings-app" data-cy={'settings-task-monitoring-page'}>
			<ForecastThemeProvider>
				<SettingsProjectSubNav />
				<Page>
					<SectionDescription>{formatMessage({id: 'settings.task-monitoring-description'})}</SectionDescription>

					{bothSet ? (
						<BothSetWarning data-cy={'task-monitoring-default-both-set'}>
							{formatMessage({id: 'settings.task-monitoring-both-set'})}
						</BothSetWarning>
					) : null}

					<OptionWrapper>
						<OptionLine>
							<RadioButton
								checked={selected === TASK_MONITORING.USE_OWNER}
								onClick={() => select(TASK_MONITORING.USE_OWNER)}
								cy={'task-monitoring-default-owner'}
							/>
							<OptionLabel>{formatMessage({id: 'project_settings.task_owner_checkbox'})}</OptionLabel>
						</OptionLine>
						<OptionDescription>
							{formatMessage({id: 'project_settings.task_owner_checkbox_desc'})}
						</OptionDescription>
					</OptionWrapper>

					<OptionWrapper>
						<OptionLine>
							<RadioButton
								checked={selected === TASK_MONITORING.USE_FOLLOWERS}
								onClick={() => select(TASK_MONITORING.USE_FOLLOWERS)}
								cy={'task-monitoring-default-followers'}
							/>
							<OptionLabel>{formatMessage({id: 'project_settings.task_follower_checkbox'})}</OptionLabel>
						</OptionLine>
						<OptionDescription>
							{formatMessage({id: 'project_settings.task_follower_checkbox_desc'})}
						</OptionDescription>
					</OptionWrapper>

					<OptionWrapper>
						<OptionLine>
							<RadioButton
								checked={selected === TASK_MONITORING.NO_TASK_MONITORING}
								onClick={() => select(TASK_MONITORING.NO_TASK_MONITORING)}
								cy={'task-monitoring-default-none'}
							/>
							<OptionLabel>{formatMessage({id: 'project_settings.do_not_use_task_monitoring'})}</OptionLabel>
						</OptionLine>
					</OptionWrapper>

					<ScopeNote>{formatMessage({id: 'settings.task-monitoring-scope-note'})}</ScopeNote>
				</Page>
			</ForecastThemeProvider>
		</div>
	);
};

export const SettingsTaskMonitoringDefaultQuery = graphql`
	query SettingsTaskMonitoringDefaultQuery {
		viewer {
			actualPersonId
			component(name: "settings_task_monitoring_default")
			...SettingsTaskMonitoringDefault_viewer
		}
	}
`;

const VIEWER_FRAGMENT = graphql`
	fragment SettingsTaskMonitoringDefault_viewer on Viewer {
		actualPersonId
		id
		company {
			id
			defaultUseTaskOwner
			defaultUseTaskFollowers
		}
	}
`;

// PIKA-558: useFragment, not createFragmentContainer — AGENTS.md (Relay, Web-Front) says
// new components consume Relay data with the hook. Existing container-based components are
// fine and don't need converting, but this file is new, so it starts on the current API.
// The query boundary stays on ForecastQueryRenderer (AdminRoutes.jsx) — useFragment only
// consumes data, it does not load it.
const SettingsTaskMonitoringDefault = ({viewer}) => {
	const data = useFragment(VIEWER_FRAGMENT, viewer);
	return <SettingsTaskMonitoringDefaultComponent viewer={data} />;
};

export default SettingsTaskMonitoringDefault;
